import {
  applyNodeChanges,
  Node as ReactFlowNode,
  NodeChange,
  useReactFlow,
  XYPosition,
} from '@xyflow/react';
import { intersection } from 'lodash';
import { useCallback, useState } from 'react';

import { Node } from '../../types';
import { HelperLinesProps } from './HelperLines';
import {
  DEFAULT_HELPER_LINES_CONFIG,
  getHelperLines,
  getHelperLinesForResize,
  HelperLinesConfig,
  ResizeHelperLinesOptions,
} from './HelperLines.utils';

/**
 * Check if changes represent an active drag operation
 */
function isDragOperation(changes: NodeChange[]): boolean {
  return (
    changes[0]?.type === 'position' &&
    'dragging' in changes[0] &&
    changes[0].dragging === true
  );
}

/**
 * Check if changes represent an active resize operation
 */
function isResizeOperation(changes: NodeChange[]): boolean {
  return changes.some((change) => change.type === 'dimensions');
}

/**
 * Check if changes represent a replace operation
 */
function isReplaceOperation(changes: NodeChange[]): boolean {
  return changes[0]?.type === 'replace';
}

/**
 * Check if helper lines should be reset
 */
function shouldResetHelperLines(changes: NodeChange[]): boolean {
  return (
    !isDragOperation(changes) &&
    !isResizeOperation(changes) &&
    !isReplaceOperation(changes)
  );
}

/**
 * Find the best helper line option for snapping based on resize context
 */
function getResizeHelperLineOptions(
  options: Array<{
    line: number;
    distance: number;
    snapPosition?: Partial<XYPosition>;
    snapDimensions?: { width?: number; height?: number };
  }>,
  isPositionChange: boolean,
  isHorizontalHelperLineOptions: boolean
) {
  if (options.length === 0) return { bestOption: undefined, allLines: [] };

  // For resize operations, we want more intelligent snapping logic
  let bestOption;

  if (isPositionChange) {
    // For position changes during resize, prefer alignment with top-left edges
    if (isHorizontalHelperLineOptions) {
      // Prefer the topmost line (smallest Y)
      bestOption = options.reduce((best, current) =>
        current.line < best.line ? current : best
      );
    } else {
      // Prefer the leftmost line (smallest X)
      bestOption = options.reduce((best, current) =>
        current.line < best.line ? current : best
      );
    }
  } else {
    // For dimension changes, prefer alignment with bottom-right edges
    if (isHorizontalHelperLineOptions) {
      // Prefer the bottommost line (largest Y)
      bestOption = options.reduce((best, current) =>
        current.line > best.line ? current : best
      );
    } else {
      // Prefer the rightmost line (largest X)
      bestOption = options.reduce((best, current) =>
        current.line > best.line ? current : best
      );
    }
  }

  // Return all line positions for display
  const allLines = options.map((option) => option.line);

  return { bestOption, allLines };
}

/**
 * Handle drag operations and update helper lines
 */
export function processDragChanges(
  changes: NodeChange[],
  nodes: Node[],
  setHorizontal: (lines?: number | number[]) => void,
  setVertical: (lines?: number | number[]) => void,
  shouldSnap: boolean,
  config: HelperLinesConfig = DEFAULT_HELPER_LINES_CONFIG
): NodeChange[] {
  const change = changes[0];

  if (!change || change.type !== 'position' || !change.position) {
    return changes;
  }

  const tileNodes = nodes.filter((node) =>
    config.nodeTypes.includes(node.type ?? '')
  );
  const helperLines = getHelperLines(change, tileNodes, 5, config);

  // Apply snapping
  if (shouldSnap) {
    change.position.x = helperLines.snapPosition.x ?? change.position.x;
    change.position.y = helperLines.snapPosition.y ?? change.position.y;
  }

  // Update helper line display
  setHorizontal(helperLines.horizontal);
  setVertical(helperLines.vertical);

  return changes;
}

/**
 * Handle resize operations and update helper lines
 */
export function processResizeChanges(
  changes: NodeChange[],
  nodes: Node[],
  setHorizontal: (lines?: number | number[]) => void,
  setVertical: (lines?: number | number[]) => void,
  shouldSnap: boolean,
  config: HelperLinesConfig = DEFAULT_HELPER_LINES_CONFIG
): NodeChange[] {
  const dimensionChange = changes.find(
    (change) => change.type === 'dimensions'
  );

  if (!dimensionChange || dimensionChange.type !== 'dimensions') {
    return changes;
  }

  const tileNodes = nodes.filter((node) =>
    config.nodeTypes.includes(node.type ?? '')
  );
  const resizeOptions: ResizeHelperLinesOptions = {
    snapDistance: 5,
    config,
  };

  const helperLines = getHelperLinesForResize(
    dimensionChange,
    tileNodes,
    resizeOptions
  );

  const isPositionChange = changes.some((change) => change.type === 'position');

  // Get helper line options using resize-specific logic
  const horizontalResult = getResizeHelperLineOptions(
    helperLines.horizontal,
    isPositionChange,
    true
  );
  const verticalResult = getResizeHelperLineOptions(
    helperLines.vertical,
    isPositionChange,
    false
  );

  // Apply snapping based on change type (use the most appropriate option for resize context)
  if (dimensionChange.dimensions && shouldSnap) {
    const newHeight = horizontalResult.bestOption?.snapDimensions?.height;
    const newWidth = verticalResult.bestOption?.snapDimensions?.width;

    if (newHeight !== undefined) {
      dimensionChange.dimensions.height = newHeight;
    }
    if (newWidth !== undefined) {
      dimensionChange.dimensions.width = newWidth;
    }
  }

  // Update helper line display (show all valid lines)
  setHorizontal(
    horizontalResult.allLines.length > 0 ? horizontalResult.allLines : undefined
  );
  setVertical(
    verticalResult.allLines.length > 0 ? verticalResult.allLines : undefined
  );

  return changes;
}

/**
 * Hook for managing helper lines during node drag and resize operations
 * @param config - Configuration for which alignment modes to enable
 */
export function useHelperLines(
  config: HelperLinesConfig = DEFAULT_HELPER_LINES_CONFIG
) {
  const [helperLineHorizontal, setHelperLineHorizontal] =
    useState<HelperLinesProps['horizontal']>(undefined);
  const [helperLineVertical, setHelperLineVertical] =
    useState<HelperLinesProps['vertical']>(undefined);

  const { getNodes } = useReactFlow();
  const reactflowNodes = getNodes();
  const draggingNodes = reactflowNodes.filter((node) => node.dragging);
  const selectedNodes = reactflowNodes.filter((node) => node.selected);

  // Only snap if there is a single node being modified
  const shouldSnap = selectedNodes.length === 1;

  const getHelperLinesChanges = useCallback(
    (changes: NodeChange[], externalNodes: Node[]): NodeChange[] => {
      // Reset helper lines if not actively dragging or resizing
      if (shouldResetHelperLines(changes)) {
        setHelperLineHorizontal(undefined);
        setHelperLineVertical(undefined);
        return changes;
      }

      // Handle drag operations
      if (isDragOperation(changes) && draggingNodes.length > 0) {
        return processDragChanges(
          changes,
          externalNodes,
          setHelperLineHorizontal,
          setHelperLineVertical,
          shouldSnap,
          config
        );
      }

      // Handle resize operations
      if (
        isResizeOperation(changes) &&
        intersection(
          selectedNodes.map((n) => n.id),
          changes.map((c) => ('id' in c ? c.id : null))
        ).length > 0
      ) {
        return processResizeChanges(
          changes,
          externalNodes,
          setHelperLineHorizontal,
          setHelperLineVertical,
          shouldSnap,
          config
        );
      }

      return changes;
    },
    [draggingNodes.length, selectedNodes, shouldSnap, config]
  );

  const applyHelperLinesToNodes = useCallback(
    (changes: NodeChange[], nodes: Node[]): Node[] => {
      return applyNodeChanges(
        getHelperLinesChanges(changes, nodes),
        nodes as ReactFlowNode[]
      ) as Node[];
    },
    [getHelperLinesChanges]
  );

  return {
    helperLineHorizontal,
    helperLineVertical,
    getHelperLinesChanges,
    applyHelperLinesToNodes,
  };
}
